/**
 * ProfilePhotoInput Component
 *
 * Componente riutilizzabile per cattura/upload foto profilo cliente.
 *
 * Features:
 * - Scatta foto con camera posteriore (default) o frontale (configurabile)
 * - Switch runtime tra camera posteriore/frontale
 * - Carica da galleria
 * - Preview con anteprima circolare
 * - Upload automatico o manuale
 * - Gestione errori con feedback visivo
 *
 * Usage:
 * <ProfilePhotoInput
 *   value={customer.foto_profilo}
 *   onChange={(url) => setCustomer({ ...customer, foto_profilo: url })}
 *   customerName="Mario Rossi"
 *   autoUpload={false}
 *   facingMode="environment"          // Default: 'environment' (posteriore)
 *   enableCameraSwitch={true}         // Switch posteriore/frontale (default: true)
 * />
 */

import { useEffect, useCallback, useState } from 'react';
import { Camera, Upload, X, Loader2, User, SwitchCamera } from 'lucide-react';
import { useProfilePhotoCapture } from '../../hooks/useProfilePhotoCapture';

export interface ProfilePhotoInputProps {
  value?: string | null;           // URL foto esistente (da DB)
  onChange: (photoUrl: string | null) => void;  // Callback quando foto cambia
  customerName?: string;            // Nome cliente per upload
  customerId?: string;              // ID cliente se esistente
  autoUpload?: boolean;             // Upload automatico dopo scatto (default: false)
  label?: string;                   // Label personalizzata
  helperText?: string;              // Testo helper
  required?: boolean;               // Campo obbligatorio
  onPhotoClick?: () => void;        // Callback quando si clicca sulla foto
  facingMode?: 'user' | 'environment';  // Camera da usare (default: 'environment' = posteriore)
  enableCameraSwitch?: boolean;     // Abilita switch camera posteriore/frontale (default: true)
}

export function ProfilePhotoInput({
  value,
  onChange,
  customerName = 'Cliente',
  customerId,
  autoUpload = false,
  label = '📸 Foto Profilo Cliente',
  helperText = 'Scatta una foto del volto del cliente per facilitare il riconoscimento',
  required = false,
  onPhotoClick,
  facingMode = 'environment',  // Default: camera posteriore
  enableCameraSwitch = true     // Default: switch abilitato
}: ProfilePhotoInputProps) {
  // Stato interno per camera facing mode (se switch abilitato)
  const [currentFacingMode, setCurrentFacingMode] = useState<'user' | 'environment'>(facingMode);

  const {
    photoUrl,
    photoFile,
    isCapturing,
    isUploading,
    uploadedUrl,
    error,
    startCapture,
    capturePhoto,
    stopCapture,
    uploadFromGallery,
    removePhoto,
    uploadPhoto,
    videoRef
  } = useProfilePhotoCapture({ facingMode: currentFacingMode });

  // Sincronizza con value esterno (quando carica cliente esistente)
  useEffect(() => {
    if (value && !photoUrl) {
      // Cliente esistente con foto già caricata
      console.log('[ProfilePhotoInput] Foto esistente caricata:', value);
    }
  }, [value, photoUrl]);

  /**
   * Gestisce l'upload manuale
   */
  const handleUpload = useCallback(async () => {
    if (!photoFile) return;

    try {
      const url = await uploadPhoto(customerName, customerId);
      onChange(url);
    } catch (error) {
      console.error('[ProfilePhotoInput] Errore upload:', error);
    }
  }, [photoFile, uploadPhoto, customerName, customerId, onChange]);

  // Auto-upload dopo scatto se abilitato
  useEffect(() => {
    if (autoUpload && photoFile && !uploadedUrl && !isUploading) {
      handleUpload();
    }
  }, [photoFile, autoUpload, uploadedUrl, isUploading, handleUpload]);

  // Notifica parent quando foto è pronta
  // ⚠️ NON includere onChange nelle dipendenze per evitare loop infiniti
  // onChange è una funzione stabile che non dovrebbe cambiare
  useEffect(() => {
    if (uploadedUrl) {
      onChange(uploadedUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedUrl]);

  /**
   * Gestisce la rimozione foto
   */
  const handleRemove = () => {
    removePhoto();
    onChange(null);
  };

  /**
   * Switch tra camera posteriore e frontale
   */
  const handleSwitchCamera = async () => {
    // Ferma camera corrente
    stopCapture();

    // Cambia facing mode
    const newMode = currentFacingMode === 'environment' ? 'user' : 'environment';
    setCurrentFacingMode(newMode);

    // Piccolo delay per permettere cleanup
    await new Promise(resolve => setTimeout(resolve, 100));

    // Riavvia camera con nuovo facing mode
    startCapture();
  };

  /**
   * Foto effettiva da mostrare (priorità: uploaded > local > value esistente)
   */
  const displayPhotoUrl = uploadedUrl || photoUrl || value;

  return (
    <div className="space-y-3">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-300">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>

      {/* Helper text */}
      {helperText && (
        <p className="text-xs text-gray-400">
          {helperText}
        </p>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* Camera capture mode */}
      {isCapturing && (
        <div className="space-y-3">
          {/* Switch camera - FUORI in alto */}
          {enableCameraSwitch && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleSwitchCamera}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-full shadow-lg transition-colors"
                title={currentFacingMode === 'environment' ? 'Passa a camera frontale' : 'Passa a camera posteriore'}
              >
                <SwitchCamera className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {currentFacingMode === 'environment' ? '📷 Posteriore' : '🤳 Frontale'}
                </span>
              </button>
            </div>
          )}

          {/* Video preview con overlay */}
          <div className="border-2 border-indigo-500 rounded-xl overflow-hidden bg-black">
            <div className="relative aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />

              {/* Overlay guida posizionamento volto */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 border-4 border-white/50 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Pulsanti controllo camera - FUORI dal riquadro */}
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={capturePhoto}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full shadow-lg transition-colors flex items-center gap-2"
            >
              <Camera className="w-5 h-5" />
              Scatta Foto
            </button>
            <button
              type="button"
              onClick={stopCapture}
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-full shadow-lg transition-colors"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Preview foto + azioni */}
      {!isCapturing && displayPhotoUrl && (
        <div className="border-2 border-green-500/30 rounded-xl p-4 bg-slate-800/50">
          <div className="flex items-center gap-4">
            {/* Preview circolare */}
            <div className="relative flex-shrink-0">
              <img
                src={displayPhotoUrl}
                alt="Foto profilo"
                className={`w-20 h-20 rounded-full object-cover border-4 border-green-500 ${
                  onPhotoClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
                }`}
                onClick={onPhotoClick}
                title={onPhotoClick ? 'Clicca per ingrandire' : undefined}
              />
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>

            {/* Info e stato */}
            <div className="flex-1 min-w-0">
              {uploadedUrl ? (
                <div>
                  <p className="text-sm text-green-400 font-medium">✓ Foto caricata</p>
                  <p className="text-xs text-gray-400">Pronta per il salvataggio</p>
                </div>
              ) : photoUrl ? (
                <div>
                  <p className="text-sm text-yellow-400 font-medium">⏳ Foto locale</p>
                  <p className="text-xs text-gray-400">
                    {autoUpload ? 'Upload in corso...' : 'Clicca "Carica" per salvare'}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-blue-400 font-medium">✓ Foto esistente</p>
                  <p className="text-xs text-gray-400">Dal database</p>
                </div>
              )}
            </div>

            {/* Azioni */}
            <div className="flex gap-2 flex-shrink-0">
              {/* Upload manuale se non auto-upload e foto non ancora caricata */}
              {!autoUpload && photoFile && !uploadedUrl && !isUploading && (
                <button
                  type="button"
                  onClick={handleUpload}
                  className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                  title="Carica foto"
                >
                  <Upload className="w-5 h-5 text-white" />
                </button>
              )}

              {/* Rimuovi */}
              <button
                type="button"
                onClick={handleRemove}
                className="p-2 bg-red-600/20 hover:bg-red-600/30 rounded-lg transition-colors"
                title="Rimuovi foto"
                disabled={isUploading}
              >
                <X className="w-5 h-5 text-red-400" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pulsanti cattura (quando nessuna foto) */}
      {!isCapturing && !displayPhotoUrl && (
        <div className="border-2 border-dashed border-gray-600 rounded-xl p-6">
          <div className="flex flex-col items-center gap-4">
            {/* Icon placeholder */}
            <div className="w-20 h-20 bg-gray-700/50 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-gray-500" />
            </div>

            {/* Pulsanti azione */}
            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={startCapture}
                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Camera className="w-5 h-5" />
                Scatta Foto
              </button>
              <button
                type="button"
                onClick={uploadFromGallery}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Da Galleria
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              {required ? 'Foto richiesta' : 'Opzionale ma consigliata'}
            </p>
          </div>
        </div>
      )}

      {/* Loading overlay durante upload */}
      {isUploading && (
        <div className="border-2 border-indigo-500/30 rounded-xl p-4 bg-indigo-900/10">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-indigo-400 animate-spin flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-indigo-300 font-medium">Upload in corso...</p>
              <p className="text-xs text-gray-400">Attendere prego</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
